import React, { useEffect, useState } from "react";
import { CELEBRATE_EVENT } from "@/lib/celebrate";

/**
 * Per-reason celebration tints.
 *
 * The default gold is Canales' brand reward color. Other reasons get a
 * thematic hue so the user can intuit the achievement type at a glance.
 */
const REASON_COLORS = {
  course_passed: "#F4B81F",   // gold — academic win
  points_earned: "#FBBF24",   // warm yellow — XP / energy
  redeem_reward: "#7C3AED",   // royal purple — prize claimed
  book_completed: "#16A34A",  // green — wisdom unlocked
  default: "#F4B81F",
};

function pickColor(reason) {
  if (!reason) return REASON_COLORS.default;
  return REASON_COLORS[reason] || REASON_COLORS.default;
}

/**
 * Global celebration overlay.
 *
 * Listens for `canales:celebrate` window events and plays a brief tinted flash
 * + radial burst. Color is driven by `event.detail.reason` so course completes
 * feel different from reward redemptions, etc.
 *
 * - Pointer-events: none → never blocks clicks.
 * - Respects prefers-reduced-motion: skips the burst, keeps a subtle static
 *   tint that fades.
 * - Sequential clicks reset the animation by bumping the key.
 */
export default function CelebrationFlash() {
  const [seq, setSeq] = useState(0);
  const [active, setActive] = useState(false);
  const [color, setColor] = useState(REASON_COLORS.default);

  useEffect(() => {
    let hideTimer = null;
    const onCelebrate = (e) => {
      const reason = e?.detail?.reason;
      setColor(pickColor(reason));
      setSeq((n) => n + 1);
      setActive(true);
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setActive(false), 1200);
    };
    window.addEventListener(CELEBRATE_EVENT, onCelebrate);
    return () => {
      window.removeEventListener(CELEBRATE_EVENT, onCelebrate);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  if (!active) return null;
  return (
    <div
      key={seq}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[70]"
      data-testid="celebration-flash"
      data-reason-color={color}
    >
      {/* Tinted backdrop */}
      <div
        className="absolute inset-0 animate-[celebrateTint_900ms_ease-out_forwards]"
        style={{ backgroundColor: color }}
      />
      {/* Centered radial burst */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full animate-[celebrateBurst_900ms_ease-out_forwards]"
        style={{
          width: "10vmax",
          height: "10vmax",
          background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        }}
      />
    </div>
  );
}
